import _, { cloneDeep } from 'lodash';
import {
  COMMON_SYNC_DIRECTION,
  CONFIG_STATUS_LEVEL,
  CONFIG_TYPES,
  CRM_ACTIVITY_SYNC_CONFIG_KEYS,
  CRM_ACTIVITY_SYNC_DEFAULT_CONFIG,
  CRM_FIELD_MAPPING_TEMPLATE,
  ENTITY_LEVEL,
  PIPELINE_STEP_BUILDER_MAP,
  SIDE_NAV_BAR_OPTIONS,
  STEP_BUILDER_MAP,
  SYNC_DIRECTION,
} from '../../constants/integrationConstants/crmConstants';
import { findDataMappingStatus } from '../crmIntegrationUtils/crmUtils';
import {
  getSideNavBarChangeLocation,
  getSideNavBarInfo,
} from '../crmIntegrationUtils/navigationUtils';
import {
  INTEGRATION_CRITERIA_TYPE,
  INTEGRATION_SETTING_TYPE,
  INTEGRATION_SYNC_TYPE,
} from '../../constants/IntegrationConstants';

export const getPreviousDeduplicationMapping = (crmState) => {
  const previousDeduplicationMapping = {
    organization: {
      ...crmState.persistedStateDeduplication.organization,
      statusObj: {
        ...crmState.persistedStateDeduplication.organization.status,
      },
      velaris: {
        ...crmState.persistedStateDeduplication.organization.velaris,
      },
      crm: {
        ...crmState.persistedStateDeduplication.organization.crm,
      },
    },
    account: {
      ...crmState.persistedStateDeduplication.account,
      statusObj: {
        ...crmState.persistedStateDeduplication.account.status,
      },
      velaris: {
        ...crmState.persistedStateDeduplication.account.velaris,
      },
      crm: {
        ...crmState.persistedStateDeduplication.account.crm,
      },
    },
  };
  return previousDeduplicationMapping;
};
export const getPreviousObjectState = (crmState) => {
  const previousObjectState = {
    organization: {
      ...crmState.persistedStateDeduplication.organization,
    },
    account: {
      ...crmState.persistedStateDeduplication.account,
    },
  };
  return previousObjectState;
};

export const generateConditionMap = (conditions) => {
  Object.keys(conditions)?.forEach((key, index) => {
    conditions[key].properties = conditions[key].properties.reduce(
      (propertyMap, { objectType, propertyValue, propertyId }) => {
        const currentIndex = `${objectType}-${propertyValue}-${propertyId}-${index}`;
        propertyMap[currentIndex] = {
          propertyIndex: currentIndex,
          propertyId,
          propertyValue,
          objectType,
        };
        return propertyMap;
      },
      {}
    );
  });
  return conditions;
};

const getEntityChangeLocation = (entityTab) => ({
  entityTab,
  sideNavBarTab:
    entityTab === ENTITY_LEVEL.ACTIVITY || entityTab === ENTITY_LEVEL.NOTE
      ? SYNC_DIRECTION.IN
      : SIDE_NAV_BAR_OPTIONS.DEDUPLICATION,
  currentConfigType: CONFIG_TYPES.DEDUPLICATION,
  syncDirection: SYNC_DIRECTION.IN,
});

export const findLocation = (
  {
    recentEntityTab,
    recentSideNavBar,
    recentStepBuilderTab,
    recentSyncDirection,
  },
  { entityTab, sideNavBarTab, stepBuilderTab }
) => {
  if (entityTab && !sideNavBarTab && !stepBuilderTab)
    return getEntityChangeLocation(entityTab);

  // If side nav bar deduplication or in sync
  if (
    sideNavBarTab === CONFIG_TYPES.IN_SYNC ||
    sideNavBarTab === CONFIG_TYPES.DEDUPLICATION ||
    sideNavBarTab === CONFIG_TYPES.PARENT_LINK
  )
    return getSideNavBarChangeLocation(
      sideNavBarTab,
      entityTab,
      recentEntityTab
    );

  if (
    sideNavBarTab === SYNC_DIRECTION.IN ||
    sideNavBarTab === SYNC_DIRECTION.OUT
  ) {
    return {
      entityTab: entityTab || recentEntityTab,
      sideNavBarTab,
      syncDirection: sideNavBarTab,
    };
  }
  // If there is a change in side nav bar.
  let currentSideNavBar;
  let currentSyncDirection;
  if (
    sideNavBarTab &&
    `${recentSideNavBar}-${recentSyncDirection}` !== sideNavBarTab
  ) {
    const { criteria, syncDirection } = getSideNavBarInfo(sideNavBarTab);
    return {
      entityTab: entityTab || recentEntityTab,
      sideNavBarTab: criteria,
      syncDirection,
      stepBuilderTab: 1,
      currentConfigType:
        entityTab === ENTITY_LEVEL.OPPORTUNITY ||
        entityTab === ENTITY_LEVEL.RISK ||
        recentEntityTab === ENTITY_LEVEL.OPPORTUNITY ||
        recentEntityTab === ENTITY_LEVEL.RISK
          ? CONFIG_TYPES.PIPELINE_DEAL_STATUS
          : CONFIG_TYPES.OBJECT_TYPE,
    };
    // If no changes in the side bar means traversing in the step builder
  }

  if (sideNavBarTab) {
    const { criteria, syncDirection } = getSideNavBarInfo(sideNavBarTab);
    currentSideNavBar = criteria;
    currentSyncDirection = syncDirection;
  }

  let currentConfigType;
  if (
    entityTab === ENTITY_LEVEL.OPPORTUNITY ||
    entityTab === ENTITY_LEVEL.RISK ||
    recentEntityTab === ENTITY_LEVEL.OPPORTUNITY ||
    recentEntityTab === ENTITY_LEVEL.RISK
  ) {
    currentConfigType = stepBuilderTab
      ? PIPELINE_STEP_BUILDER_MAP[stepBuilderTab]
      : PIPELINE_STEP_BUILDER_MAP[recentStepBuilderTab];
  } else {
    currentConfigType = stepBuilderTab
      ? STEP_BUILDER_MAP[stepBuilderTab]
      : STEP_BUILDER_MAP[recentStepBuilderTab];
  }
  return {
    entityTab: entityTab || recentEntityTab,
    sideNavBarTab: currentSideNavBar || recentSideNavBar,
    stepBuilderTab: stepBuilderTab || recentStepBuilderTab,
    currentConfigType,
    syncDirection: currentSyncDirection || recentSyncDirection,
  };
};

export const deepCloneDataMappingTemplate = () =>
  JSON.parse(JSON.stringify(CRM_FIELD_MAPPING_TEMPLATE));

export const deepCloneTemplate = (template) =>
  JSON.parse(JSON.stringify(template));

const deStructureDataMappingObj = (
  state,
  syncDirection,
  entityLevel,
  criteriaType
) => {
  return {
    ...state[CONFIG_TYPES.DATA_MAPPING],
    [syncDirection]: {
      ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection],
      [entityLevel]: {
        ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel],
        [criteriaType]: {
          ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel][
            criteriaType
          ],
          dataMappings: {
            ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel][
              criteriaType
            ].dataMappings,
          },
        },
      },
    },
  };
};

export const getAddDataMapRowState = (
  state,
  entityLevel,
  criteriaType,
  dataMappingId,
  syncDirection,
  mappingType
) => {
  if (mappingType === CONFIG_TYPES.DATA_MAPPING) {
    const newDataMappingState = deStructureDataMappingObj(
      state,
      syncDirection,
      entityLevel,
      criteriaType
    );
    newDataMappingState[syncDirection][entityLevel][criteriaType].dataMappings[
      dataMappingId
    ] = {
      velaris: {},
      dataMappingId,
      crm: {},
      status: CONFIG_STATUS_LEVEL.IN_PROGRESS,
    };
    newDataMappingState[syncDirection][entityLevel][criteriaType].status =
      CONFIG_STATUS_LEVEL.IN_PROGRESS;
    return newDataMappingState;
  }
  const newInSyncDataMappingState = {
    ...state[CONFIG_TYPES.IN_SYNC],
    [entityLevel]: {
      ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
      dataMappings: {
        ...state[CONFIG_TYPES.IN_SYNC][entityLevel].dataMappings,
        [dataMappingId]: {
          velaris: {},
          dataMappingId,
          crm: {},
          status: CONFIG_STATUS_LEVEL.IN_PROGRESS,
        },
      },
      status: CONFIG_STATUS_LEVEL.IN_PROGRESS,
    },
  };
  return newInSyncDataMappingState;
};
export const getChangeDataMappingState = ({
  state,
  entityLevel,
  criteriaType,
  syncDirection,
  dataMappingId,
  side,
  propertyId,
  mappingType,
}) => {
  if (mappingType === CONFIG_TYPES.DATA_MAPPING) {
    const newDataMappingState = deStructureDataMappingObj(
      state,
      syncDirection,
      entityLevel,
      criteriaType
    );
    newDataMappingState[syncDirection][entityLevel][criteriaType].dataMappings[
      dataMappingId
    ] = {
      ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel][
        criteriaType
      ].dataMappings[dataMappingId],
      [side]: { fieldId: propertyId },
    };
    const newDataMappingStatus = findDataMappingStatus(
      newDataMappingState[syncDirection][entityLevel][criteriaType].dataMappings
    );
    newDataMappingState[syncDirection][entityLevel][criteriaType].status =
      newDataMappingStatus;
    return newDataMappingState;
  }
  const newInSyncDataMappingState = {
    ...state[CONFIG_TYPES.IN_SYNC],
    [entityLevel]: {
      ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
      dataMappings: {
        ...state[CONFIG_TYPES.IN_SYNC][entityLevel].dataMappings,
        [dataMappingId]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel].dataMappings[
            dataMappingId
          ],
          [side]: { fieldId: propertyId },
        },
      },
    },
  };
  const newInSyncDataMappingStatus = findDataMappingStatus(
    newInSyncDataMappingState[entityLevel].dataMappings
  );
  newInSyncDataMappingState[entityLevel].status = newInSyncDataMappingStatus;
  return newInSyncDataMappingState;
};
export const getChangeDataMappingwithFieldConfigState = ({
  state,
  entityLevel,
  criteriaType,
  syncDirection,
  dataMappingId,
  mappingType,
  fieldConfig,
}) => {
  if (mappingType === CONFIG_TYPES.DATA_MAPPING) {
    const newDataMappingState = deStructureDataMappingObj(
      state,
      syncDirection,
      entityLevel,
      criteriaType
    );
    newDataMappingState[syncDirection][entityLevel][criteriaType].dataMappings[
      dataMappingId
    ] = {
      ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel][
        criteriaType
      ].dataMappings[dataMappingId],
      fieldConfig,
    };
    const newDataMappingStatus = findDataMappingStatus(
      newDataMappingState[syncDirection][entityLevel][criteriaType].dataMappings
    );
    newDataMappingState[syncDirection][entityLevel][criteriaType].status =
      newDataMappingStatus;
    return newDataMappingState;
  }
  const newInSyncDataMappingState = {
    ...state[CONFIG_TYPES.IN_SYNC],
    [entityLevel]: {
      ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
      dataMappings: {
        ...state[CONFIG_TYPES.IN_SYNC][entityLevel].dataMappings,
        [dataMappingId]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel].dataMappings[
            dataMappingId
          ],
          fieldConfig,
        },
      },
    },
  };
  const newInSyncDataMappingStatus = findDataMappingStatus(
    newInSyncDataMappingState[entityLevel].dataMappings
  );
  newInSyncDataMappingState[entityLevel].status = newInSyncDataMappingStatus;
  return newInSyncDataMappingState;
};

export const extractActivityTypeMapping = (
  velarisActivityType,
  activityTypeMapping
) => {
  const [existingMapping, otherMappings] = activityTypeMapping.reduce(
    ([_existingMapping, _otherMappings], mapping) => {
      if (mapping.velaris === velarisActivityType) {
        return [mapping, _otherMappings];
      }
      return [_existingMapping, [..._otherMappings, mapping]];
    },
    [null, []]
  );

  return [existingMapping, otherMappings];
};

export const updateCrmActivityTypeStatus = (
  mappedActivityTypes,
  allActivityTypes
) => {
  const crmActivityTypes = cloneDeep(allActivityTypes);

  return crmActivityTypes.reduce((acc, activityType) => {
    const { activityCategory, id } = activityType;
    const alreadyMapped = mappedActivityTypes.find((mapping) =>
      mapping.vendor[activityCategory]?.includes(id)
    );

    if (alreadyMapped) {
      return [...acc, { ...activityType, used: true }];
    }

    return [...acc, { ...activityType, used: false }];
  }, []);
};

export function ActivitySyncConfigBuilder(configs) {
  this.config = {};
  this.configs = configs || [];

  this.addConfig = (key, value) => {
    this.config = {
      ...this.config,
      [key]: value,
    };
  };

  this.extractSyncStatus = () => {
    const syncStatus = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.NA &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.NA &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    if (syncStatus === undefined) {
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_STATUS, null);
    } else {
      this.addConfig(
        CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_STATUS,
        syncStatus.enable === 1
      );
    }
  };

  this.extractEventDrivenSyncStatus = () => {
    const createActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.EVENT_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.CREATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    const updateActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.EVENT_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.UPDATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    if (createActivities === undefined && updateActivities === undefined) {
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_TYPE, null);
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.HISTORICAL_SYNC, null);
    } else {
      const isEnabled =
        createActivities?.enable === 1 || updateActivities?.enable === 1;
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.HISTORICAL_SYNC, isEnabled);
    }
  };

  this.extractDataDrivenSyncStatus = () => {
    const createActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.DATA_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.CREATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    const updateActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.DATA_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.UPDATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    if (createActivities === undefined && updateActivities === undefined) {
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_TYPE, null);
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.DAILY_SYNC, null);
    } else {
      const isEnabled =
        createActivities?.enable === 1 || updateActivities?.enable === 1;
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.DAILY_SYNC, isEnabled);
    }
  };

  this.extractSyncTypeStatus = () => {
    const eventDrivenCreateActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.EVENT_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.CREATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    const eventDrivenUpdateActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.EVENT_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.UPDATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    const dataDrivenCreateActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.DATA_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.CREATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    const dataDrivenUpdateActivities = this.configs.find(
      (config) =>
        config.syncType === INTEGRATION_SYNC_TYPE.DATA_DRIVEN &&
        config.criteria === INTEGRATION_CRITERIA_TYPE.UPDATE &&
        config.syncDirection === COMMON_SYNC_DIRECTION.IN
    );

    const createEnabled =
      eventDrivenCreateActivities?.enable === 1 ||
      dataDrivenCreateActivities?.enable === 1;
    const updateEnabled =
      eventDrivenUpdateActivities?.enable === 1 ||
      dataDrivenUpdateActivities?.enable === 1;

    if (createEnabled && updateEnabled) {
      this.addConfig(
        CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_TYPE,
        INTEGRATION_CRITERIA_TYPE.UPDATE
      );
    } else if (createEnabled) {
      this.addConfig(
        CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_TYPE,
        INTEGRATION_CRITERIA_TYPE.CREATE
      );
    } else {
      this.addConfig(CRM_ACTIVITY_SYNC_CONFIG_KEYS.SYNC_TYPE, null);
    }
  };

  this.getConfig = () => {
    return this.config;
  };
}

export const extractActivitySyncConfigs = (configs) => {
  const activitySyncConfigs = Object.values(configs).filter(
    ({ settingType }) => settingType === INTEGRATION_SETTING_TYPE.ACTIVITY_SYNC
  );

  const configBuilder = new ActivitySyncConfigBuilder(activitySyncConfigs);
  configBuilder.extractSyncStatus();
  configBuilder.extractEventDrivenSyncStatus();
  configBuilder.extractDataDrivenSyncStatus();
  configBuilder.extractSyncTypeStatus();
  return configBuilder.getConfig();
};

export const setActivityConfigDefaultValues = (configs) => {
  return Object.entries(configs).reduce((acc, [key, value]) => {
    if (value === null) {
      return { ...acc, [key]: CRM_ACTIVITY_SYNC_DEFAULT_CONFIG[key] };
    }
    return { ...acc, [key]: value };
  }, {});
};

export const checkIfActivitySyncConfigChanged = (
  currentConfig,
  savedConfig,
  currentMapping,
  savedMapping
) => {
  return (
    !_.isEqual(currentConfig, savedConfig) ||
    !_.isEqual(currentMapping, savedMapping)
  );
};

export const checkAnyInCompleteRowInPipelineMapping = (pipelineMapping) => {
  if (!pipelineMapping.length) return true;
  // array = [{vendorPipeline: "pipeline1", pipelineStatus: [{velarisPipelineStatus: 'open', vendorPipelineStages: ['stage1', 'stage2']}]
  // check if any row is incomplete
  // if pipelinestatus array is empty, return true
  return pipelineMapping.some((pipeline) => {
    if (!pipeline.pipelineStatus.length) return true;

    return pipeline.pipelineStatus.some(
      (status) =>
        !status.velarisPipelineStatus || !status.vendorPipelineStages.length
    );
  });
};
